import React from 'react';
import styled from 'styled-components';

import { LFC } from '../Legacy';
import { ActionItem } from './ActionItem';
import { Cell, Grid } from './Grid';
import { toRem } from './Utils';

export type ActionLayoutProps = {
    id?: string,
    className?: string,
    style?: any,
    mode?: string,
    lines?: boolean,
    padding: 1 | 2 | 3 | 4,
};

//
// Grid Layout
//

type GridLayoutProps = Omit<ActionLayoutProps, 'mode'> & { columns: number };

const gridPadding = (p: GridLayoutProps) => `padding: ${toRem(.3125 * (p.padding))};`;
const gridBorder = ({ i, columns, rows, lines }: any) => {
    if (lines) {
        const right = ((i % columns) + 1) < columns ? 'border-right: solid 2px #F2F2F2;' : '';
        const bottom = (Math.floor(i / columns) + 1) < rows ? 'border-bottom: solid 2px #F2F2F2;' : '';
        return right + bottom;
    }
    return '';
};

const GridFrame = styled.div`
    position: relative;
`;

const GridCell = styled<any>(Cell)`
    ${ActionItem} {
        display: flex;
        align-items: center;
        ${p => gridBorder(p)}
        ${p => gridPadding(p as any)}
    }
`;

const GridLayout: LFC<GridLayoutProps> = ({ id, className, style, ...props }) => {
    return <GridFrame {...{ id, className, style }}>
        <Grid columns={props.columns}>
            {React.Children.map(props.children, (x, i) => {
                return <GridCell {...props} i={i} rows={(Math.ceil(React.Children.count(props.children) / props.columns))}>
                    {x}
                </GridCell>;
            })}
        </Grid>
    </GridFrame >;
};


//
// Strip Layout
//

const isHorizontal = (p: ActionLayoutProps) => p.mode !== 'column' && p.mode !== 'column-reverse';
const stripPadding = (p: ActionLayoutProps) => `padding: ${toRem(.3125 * (p.padding))};`;
const stripBorder = (p: ActionLayoutProps) => {
    if (p.lines) {
        return isHorizontal(p)
            ? 'border-left: solid 2px #F2F2F2;'
            : 'border-top: solid 2px #F2F2F2;';
    }
    return '';
};

const StripFrame = styled.div<ActionLayoutProps>`
    position: relative;
    display: flex;
	flex-direction: ${p => p.mode};

    // These weird selectors are to enable ActionItems to be wrapped in tooltips and other divs:

    & > * {
        display: inline-flex;

        ${p => stripBorder(p)}

        &:first-child {
            border: none;
            border-top-left-radius: 0.375rem;
            ${p => isHorizontal(p) ? '' : 'border-top-right-radius: 0.375rem'};
            ${p => isHorizontal(p) ? 'border-bottom-left-radius: 0.375rem' : ''};
        }

        &:last-child {
            ${p => isHorizontal(p) ? 'border-top-right-radius: 0.375rem' : ''};  
            ${p => isHorizontal(p) ? '' : 'border-bottom-left-radius: 0.375rem'};  
            border-bottom-right-radius: 0.375rem;
        }
    }

    & > ${ActionItem}, & > * > ${ActionItem} {
        ${p => stripPadding(p)}
    }
`;

const StripLayout: LFC<ActionLayoutProps> = (props) => {
    return <StripFrame {...props}>
        {props.children}
    </StripFrame>;
};

const parseMode = (mode?: string): any => {
    mode = (mode || 'column').trim().toLowerCase();
    if (mode.startsWith('grid')) {
        const cols = mode.split(':')[1];
        return ['grid', parseInt(cols) || 1];
    }
    else {
        return [mode];
    }
};


//
// Component
//

export const ActionLayout: LFC<ActionLayoutProps> = ({ mode, ...props }) => {
    const [m, c] = parseMode(mode ?? 'row');
    return m === 'grid'
        ? <GridLayout columns={c} {...props} />
        : <StripLayout mode={mode} {...props} />;
};
